// Shared display helpers for the media-package surfaces.
//
// Status labels, badge classes, date formatting. Kept in one place so the
// admin Media page, AccountDetail MediaTab, school portal Media Calendar,
// and Media-role dashboard all show consistent badges and copy.

export const PACKAGE_STATUSES = ['active', 'expired', 'exhausted', 'cancelled', 'suspended'];

export const EVENT_STATUSES = [
  'pending', 'booked', 'shooting', 'editing',
  'delivered', 'cancelled', 'rescheduled_out',
];

export const NOTICE_TIERS = ['standard', 'late', 'rush', 'emergency'];

const PACKAGE_STATUS_BADGE = {
  active:    'bg-[#B0FC38]/[0.1] border border-[#B0FC38]/30 text-[#B0FC38]',
  expired:   'bg-white/[0.04] border border-white/[0.08] text-gray-400',
  exhausted: 'bg-white/[0.04] border border-white/[0.08] text-gray-400',
  cancelled: 'bg-red-500/[0.1] border border-red-400/30 text-red-300',
  suspended: 'bg-amber-500/[0.1] border border-amber-400/30 text-amber-300',
};

const PACKAGE_STATUS_LABEL = {
  active:    'Active',
  expired:   'Expired',
  exhausted: 'Exhausted',
  cancelled: 'Cancelled',
  suspended: 'Suspended',
};

const EVENT_STATUS_BADGE = {
  pending:          'bg-amber-500/[0.1] border border-amber-400/30 text-amber-300',
  booked:           'bg-[#B0FC38]/[0.1] border border-[#B0FC38]/30 text-[#B0FC38]',
  shooting:         'bg-sky-400/[0.1] border border-sky-400/30 text-sky-300',
  editing:          'bg-sky-400/[0.1] border border-sky-400/30 text-sky-300',
  delivered:        'bg-[#B0FC38]/[0.15] border border-[#B0FC38]/40 text-[#B0FC38]',
  cancelled:        'bg-red-500/[0.1] border border-red-400/30 text-red-300',
  rescheduled_out:  'bg-white/[0.04] border border-white/[0.08] text-gray-400',
};

const EVENT_STATUS_LABEL = {
  pending:         'Pending',
  booked:          'Booked',
  shooting:        'Shooting',
  editing:         'Editing',
  delivered:       'Delivered',
  cancelled:       'Cancelled',
  rescheduled_out: 'Rescheduled',
};

const NOTICE_TIER_BADGE = {
  standard:  'bg-white/[0.04] border border-white/[0.08] text-gray-400',
  late:      'bg-yellow-500/[0.1] border border-yellow-400/30 text-yellow-300',
  rush:      'bg-orange-500/[0.1] border border-orange-400/30 text-orange-300',
  emergency: 'bg-red-500/[0.1] border border-red-400/30 text-red-300',
};

const NOTICE_TIER_LABEL = {
  standard:  'Standard',
  late:      'Late · +$250',
  rush:      'Rush · +$500',
  emergency: 'Emergency · +$750',
};

export function packageStatusLabel(status)       { return PACKAGE_STATUS_LABEL[status] || status; }
export function packageStatusBadgeClass(status)  { return PACKAGE_STATUS_BADGE[status]  || PACKAGE_STATUS_BADGE.expired; }
export function eventStatusLabel(status)         { return EVENT_STATUS_LABEL[status]    || status; }
export function eventStatusBadgeClass(status)    { return EVENT_STATUS_BADGE[status]    || EVENT_STATUS_BADGE.rescheduled_out; }
export function noticeTierLabel(tier)            { return NOTICE_TIER_LABEL[tier]       || tier; }
export function noticeTierBadgeClass(tier)       { return NOTICE_TIER_BADGE[tier]       || NOTICE_TIER_BADGE.standard; }

// "Apr 22" / "Apr 22, 2027" — formatted in NYC local time since all NexSav
// scheduling is ET. Year only shown when it isn't the current year.
export function formatExpiryDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '—';
  const opts = { month: 'short', day: 'numeric', timeZone: 'America/New_York' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

// "Apr 22, 10:00 AM – 2:00 PM"
export function formatMediaDateRange(startIso, endIso) {
  if (!startIso) return '—';
  const start = new Date(startIso);
  if (Number.isNaN(start.valueOf())) return '—';
  const dateStr = start.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'America/New_York',
  });
  const startTime = start.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  });
  if (!endIso) return `${dateStr}, ${startTime}`;
  const end = new Date(endIso);
  if (Number.isNaN(end.valueOf())) return `${dateStr}, ${startTime}`;
  const endTime = end.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York',
  });
  return `${dateStr}, ${startTime} – ${endTime}`;
}

// "in 6 days" / "expired 3 days ago" / "today" — used in expiration
// warning copy.
export function relativeDays(iso) {
  if (!iso) return '';
  const target = new Date(iso);
  if (Number.isNaN(target.valueOf())) return '';
  const diffDays = Math.round((target - new Date()) / 86_400_000);
  if (diffDays === 0) return 'today';
  if (diffDays > 0) return `in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
  const past = Math.abs(diffDays);
  return `${past} day${past === 1 ? '' : 's'} ago`;
}

// ---------------------------------------------------------------------------
// Media package catalog — one product, variable event count. Mirrors
// functions/_lib/media-package-catalog.js (keep the two in step).
// ---------------------------------------------------------------------------
export const MEDIA_PACKAGE_PRODUCT_TYPE = 'media_package';
export const MEDIA_PACKAGE_MAX_EVENTS = 30;
// Published price points; anything else defaults to the per-event rate.
export const MEDIA_PACKAGE_LIST_PRICE_DOLLARS = Object.freeze({ 5: 7500, 7: 9500 });
export const MEDIA_PACKAGE_PER_EVENT_DOLLARS = 1500;
// Quick picks surfaced in the New Project dialog + line-item editor.
export const MEDIA_PACKAGE_QUICK_PICKS = Object.freeze([5, 7]);

export function clampMediaPackageEvents(value) {
  return Math.min(MEDIA_PACKAGE_MAX_EVENTS, Math.max(1, parseInt(value, 10) || 1));
}
export function mediaPackageDescription(events) {
  const n = Number(events) || 1;
  return `Media Package (${n} ${n === 1 ? 'event' : 'events'}, 12-month validity)`;
}
export function mediaPackageListPriceDollars(events) {
  const n = Number(events) || 0;
  return MEDIA_PACKAGE_LIST_PRICE_DOLLARS[n] ?? n * MEDIA_PACKAGE_PER_EVENT_DOLLARS;
}
// Legacy tiered/custom product types from before the catalog was flattened
// still appear on historical invoices; treat them as media packages too.
export function isMediaPackageProductType(productType) {
  return productType === MEDIA_PACKAGE_PRODUCT_TYPE
    || (typeof productType === 'string' && productType.startsWith('media_package_'));
}

// ---------------------------------------------------------------------------
// Booking questionnaire options — shared by the portal booking form and the
// admin questionnaire editor so the two always offer the same choices.
// ---------------------------------------------------------------------------
export const MEDIA_EVENT_CATEGORIES = Object.freeze([
  { value: '', label: 'Pick one…' },
  { value: 'graduation', label: 'Graduation' },
  { value: 'concert', label: 'Concert' },
  { value: 'sports', label: 'Sports' },
  { value: 'field_trip', label: 'Field trip' },
  { value: 'ceremony', label: 'Ceremony' },
  { value: 'open_house', label: 'Open house' },
  { value: 'other', label: 'Other' },
]);
export const MEDIA_EVENT_TONES = Object.freeze([
  { value: '', label: 'Pick one…' },
  { value: 'formal', label: 'Formal' },
  { value: 'celebratory', label: 'Celebratory' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'mixed', label: 'Mixed' },
]);
// Coverage is capped at 4 hours (spec §3); 30-min steps up to that.
export const MEDIA_DURATION_OPTIONS = Object.freeze([
  { value: 1, label: '1 hour' },
  { value: 1.5, label: '1.5 hours' },
  { value: 2, label: '2 hours' },
  { value: 2.5, label: '2.5 hours' },
  { value: 3, label: '3 hours' },
  { value: 3.5, label: '3.5 hours' },
  { value: 4, label: '4 hours' },
]);

// <input type="datetime-local"> speaks zone-less "YYYY-MM-DDTHH:MM" in the
// browser's zone; the API speaks UTC ISO. Convert at the edge, both ways.
export function localToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.valueOf()) ? null : d.toISOString();
}
export function isoToLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function addHoursToLocal(local, hours) {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.valueOf())) return '';
  return isoToLocal(new Date(d.getTime() + hours * 3600 * 1000).toISOString());
}
